import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Brain, 
  Code, 
  Smartphone, 
  Link2, 
  Sparkles,
  CheckCircle2,
  Users,
  Award,
  TrendingUp,
  ChevronRight,
  Play,
  Star,
  Quote,
  ShoppingCart,
  Briefcase,
  Database,
  Cog,
  Server,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Home = () => {
  const services = [
    {
      icon: Brain,
      title: 'AI Integration',
      description: 'Transform your business with intelligent AI solutions that automate processes and enhance decision-making.',
      color: 'from-blue-500 to-indigo-600',
    },
   
    {
      icon: Smartphone,
      title: 'App Development',
      description: 'Create powerful mobile applications for iOS and Android that engage users and drive growth.',
      color: 'from-orange-500 to-red-600',
    },
    
    {
      icon: ShoppingCart,
      title: 'Shopify Solutions',
      description: 'Optimize your e-commerce store with custom Shopify solutions, app development, and strategic integrations to maximize sales.',
      color: 'from-green-500 to-emerald-600',
    },
    {
      icon: Briefcase,
      title: 'NetSuite Solutions',
      description: 'Streamline your business operations with NetSuite implementations, customizations, and expert consulting services.',
      color: 'from-amber-500 to-orange-600',
    },
    {
      icon: Database,
      title: 'ERP Solutions',
      description: 'Deploy comprehensive enterprise resource planning systems to unify your business processes and improve efficiency.',
      color: 'from-rose-500 to-pink-600',
    },
    {
      icon: Cog,
      title: 'IT Services & Support',
      description: 'Get reliable IT support, infrastructure management, and technical consulting to keep your systems running smoothly.',
      color: 'from-slate-500 to-slate-700',
    },
    {
      icon: Server,
      title: 'Cloud Infrastructure',
      description: 'Build scalable cloud solutions with AWS, Azure, or Google Cloud for reliable and flexible infrastructure.',
      color: 'from-indigo-500 to-blue-600',
    },
    {
      icon: Shield,
      title: 'Cybersecurity Solutions',
      description: 'Protect your business with comprehensive cybersecurity services, threat assessment, and compliance management.',
      color: 'from-red-500 to-rose-600',
    },
  ];

  const stats = [
    { value: '150+', label: 'Projects Delivered', icon: CheckCircle2 },
    { value: '50+', label: 'Happy Clients', icon: Users },
    { value: '99%', label: 'Client Satisfaction', icon: Award },
    { value: '5+', label: 'Years Experience', icon: TrendingUp },
  ];

  const testimonials = [
    {
      name: 'Peter DiOrio',
      role: 'RavLink',
      content: 'I’m a one-man MSP. And JnanIT is my secret ingredient. Kudos guys!.',
      avatar: 'PD',
    },
    {
      name: 'Sean',
      role: 'SMC',
      content: 'They handle our on-prem, cloud infra, and migration headaches. They also support us with Visual ERP. Super.',
      avatar: 'SC',
    },
    {
      name: 'Aina',
      role: 'Pets Global',
      content: 'These guys are our trusted advisors and one-stop shop for all things IT.',
      avatar: 'AG',
    },
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient mesh-gradient">
        {/* Floating Shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="stagger-children">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">AI-Powered Innovation</span>
              </div>
              
              <h1 
                data-testid="hero-title"
                className="text-5xl md:text-7xl font-bold font-['Outfit'] tracking-tighter mb-6 leading-tight"
              >
                Unlock the Power of{' '}
                <span className="gradient-text">AI Integration</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
                We build intelligent solutions that transform businesses. From AI integration 
                to custom development, we turn your vision into reality.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="hero-cta-primary"
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all btn-shine group"
                  >
                    Start Your Project
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/product">
                  <Button 
                    data-testid="hero-cta-secondary"
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-semibold border-slate-200 hover:bg-slate-50 group"
                  >
                    <Play className="mr-2 w-5 h-5 text-blue-600" />
                    See Setu Connect
                  </Button>
                </Link>
              </div>

              {/* Trust Badges */}
              {/*<div className="mt-12 flex items-center gap-6">
                <div className="flex -space-x-3">
                  {['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500'].map((bg, i) => (
                    <div key={i} className={`w-10 h-10 rounded-full ${bg} border-2 border-white flex items-center justify-center text-white text-xs font-bold`}>
                      {String.fromCharCode(65 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-500">Trusted by 50+ companies</p>
                </div>
              </div> */}
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1754738381739-5efb94a4525b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwYWklMjB0ZWNobm9sb2d5JTIwYWJzdHJhY3QlMjBibHVlJTIwcHVycGxlfGVufDB8fHx8MTc3MTg1NzE0Mnww&ixlib=rb-4.1.0&q=85"
                  alt="AI Technology Abstract"
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
                />
                {/* Floating Card */}
                {/* <div className="absolute  -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">150+ Projects</p>
                      <p className="text-sm text-slate-500">Successfully Delivered</p>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
            
             {/* Floating Card */}
            {/* <div className="absolute  bottom-8 right-8 bg-white rounded-2xl shadow-xl p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">150+ Projects</p>
                      <p className="text-sm text-slate-500">Successfully Delivered</p>
                    </div>
                  </div>
                </div> */}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Our Services
            </span>
            <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-4">
              Solutions That Drive <span className="gradient-text">Innovation</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We offer comprehensive technology services to help your business thrive in the digital age.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card 
                key={index}
                data-testid={`service-card-${index}`}
                className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-blue-100 card-hover"
              >
                <CardContent className="p-0">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold font-['Outfit'] mb-3 text-slate-900">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  {/* <Link 
                    to="/services" 
                    className="inline-flex items-center text-blue-600 font-medium group-hover:gap-2 transition-all"
                  >
                    Learn More
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link> */}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* And Much More Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* <p className="text-lg text-blue-300 mb-4 font-medium">
            <Quote className="w-6 h-6 inline-block mr-2 -mt-1" />
            Comprehensive Solutions
          </p> */}
          <h2 className="text-5xl md:text-6xl font-bold font-['Outfit'] text-white mb-6">
           Comprehensive <span className="gradient-text">Solutions</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Beyond these core services, our team is ready to tackle any technology challenge your business faces. 
            Whether it's a unique business requirement or an emerging technology trend, we have the expertise and 
            passion to deliver innovative solutions tailored to your specific needs.
          </p>
          <div className="mt-10">
            <Link to="/services">
              <Button 
                size="lg"
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 font-semibold shadow-lg group"
              >
                Explore All Services
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Highlight - Setu AI */}
      <section data-testid="product-highlight" className="py-24 bg-slate-50 mesh-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-purple-100 text-purple-700 text-sm font-medium mb-4">
                Featured Product
              </span>
              <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-6">
                Introducing <span className="gradient-text">Setu Connect - AI Powered</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Experience the future of ecommerce integration with AI-powered automation. Setu Connect 
                intelligently connects your ecommerce platforms to your ERP systems, automating order syncing, 
                inventory management, and financial data flow in real-time with advanced machine learning capabilities.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Support for Shopify, Faire, Walmart, Amazon & MarketTime',
                  'Integrate with NetSuite, Zoho & other ERPs',
                  'Real-time bidirectional data synchronization',
                  'Automated inventory & order management',
                  'Intelligent error handling & notifications',
                  '24/7 monitoring & support'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/product">
                <Button 
                  data-testid="setu-connect-cta"
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-8 py-6 font-semibold shadow-lg shadow-blue-500/25 group"
                >
                  Explore Setu Connect
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-3xl blur-3xl" />
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                      <Link2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Setu Connect</h4>
                      <p className="text-sm text-slate-500">AI-Powered Integration Platform</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                    Active
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Ecommerce Platforms</p>
                    <div className="grid grid-cols-3 gap-2">
                      {['Shopify', 'Faire', 'Walmart', 'Amazon', 'MarketTime', 'Tiktok'].map((platform, i) => (
                        <div key={i} className="flex items-center justify-center p-2 bg-slate-50 rounded-lg">
                          <span className="text-xs font-medium text-slate-700">{platform}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-center py-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-white rotate-90" />
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-3">ERP Systems</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['NetSuite', 'Zoho'].map((erp, i) => (
                        <div key={i} className="flex items-center justify-center p-2 bg-slate-50 rounded-lg">
                          <span className="text-xs font-medium text-slate-700">{erp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Active Integrations</span>
                    <span className="font-bold text-slate-900 font-mono-numbers">10+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Testimonials
            </span>
            <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-4">
              What Our <span className="gradient-text">Clients Say</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our clients have to say about working with us.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index}
                data-testid={`testimonial-card-${index}`}
                className="testimonial-card bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-0">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 cta-gradient relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how our AI solutions and development expertise can help you achieve your goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                data-testid="cta-contact-button"
                size="lg"
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl group"
              >
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/services">
              <Button 
                data-testid="cta-services-button"
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg font-semibold text-white border-white/30 hover:bg-white/10 transition-colors"
              >
                View Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
